/**
 * IntelliHealth Chat WebSocket Service
 *
 * Singleton managing the raw WebSocket lifecycle for NovaMed chat:
 * connect/disconnect, send, reconnect with exponential backoff,
 * message parsing and routing by event type.
 *
 * Follows the twilioMessagingService singleton pattern.
 */

import type {
  ConnectionStatus,
  IntelliHealthChatAction,
  IntelliHealthChatEvent,
  ServerMessage,
} from '@/types/intelliHealthChat';

type MessageCallback = (event: IntelliHealthChatEvent, data: unknown) => void;
type StatusCallback = (status: ConnectionStatus) => void;

// Backoff parameters
const INITIAL_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
const MAX_RETRIES = 5;

// Threshold: if connection fails faster than this, it's likely auth rejection
const FAST_FAILURE_MS = 2000;

function isLocalhost(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

class IntelliHealthChatService {
  private ws: WebSocket | null = null;
  private connectionStatus: ConnectionStatus = 'disconnected';
  private intentionalDisconnect = false;
  private isConnecting = false;

  // Reconnect state
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private fastFailureCount = 0;
  private lastConnectUrl = '';

  // Callback registrations
  private messageCallbacks = new Set<MessageCallback>();
  private statusCallbacks = new Set<StatusCallback>();

  /**
   * Connect to the WebSocket endpoint.
   * Appends ?token= for non-localhost URLs.
   */
  connect(url: string, token?: string): void {
    if (this.isConnecting || this.connectionStatus === 'connected') {
      return;
    }

    this.isConnecting = true;
    this.intentionalDisconnect = false;
    this.lastConnectUrl = url;
    this.setStatus('connecting');

    let wsUrl = url;
    if (token && !isLocalhost(url)) {
      const separator = url.includes('?') ? '&' : '?';
      wsUrl = `${url}${separator}token=${token}`;
    }

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (err) {
      console.error('[IntelliHealthChat] WebSocket construction failed:', err);
      this.isConnecting = false;
      this.setStatus('disconnected');
      return;
    }

    const connectTime = Date.now();

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.fastFailureCount = 0;
      this.setStatus('connected');
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const parsed: ServerMessage = JSON.parse(event.data);
        if (!parsed.event) {
          console.warn('[IntelliHealthChat] Message missing event field:', parsed);
          return;
        }
        this.messageCallbacks.forEach((cb) => cb(parsed.event, parsed.data));
      } catch {
        console.warn('[IntelliHealthChat] Malformed message, ignoring:', event.data);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.isConnecting = false;
      this.ws = null;

      if (this.intentionalDisconnect || event.code === 1000) {
        this.setStatus('disconnected');
        return;
      }

      // Abnormal close — attempt reconnect
      const elapsed = Date.now() - connectTime;
      this.attemptReconnect(elapsed < FAST_FAILURE_MS);
    };

    this.ws.onerror = () => {
      // onerror is always followed by onclose, so we handle reconnect there
      this.isConnecting = false;
    };
  }

  /**
   * Intentionally disconnect. No reconnect will be attempted.
   */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.isConnecting = false;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.fastFailureCount = 0;

    if (this.ws) {
      this.ws.close(1000);
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  /**
   * Send an action to the backend.
   * Returns true if sent, false if socket is not open.
   */
  send(action: IntelliHealthChatAction, data: Record<string, unknown>): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[IntelliHealthChat] Cannot send, socket not open. Action:', action);
      return false;
    }

    const message = JSON.stringify({ event: action, data });
    this.ws.send(message);
    return true;
  }

  /**
   * Register a callback for incoming messages.
   * Returns an unsubscribe function.
   */
  onMessage(callback: MessageCallback): () => void {
    this.messageCallbacks.add(callback);
    return () => this.messageCallbacks.delete(callback);
  }

  /**
   * Register a callback for connection status changes.
   * Returns an unsubscribe function.
   */
  onStatusChange(callback: StatusCallback): () => void {
    this.statusCallbacks.add(callback);
    return () => this.statusCallbacks.delete(callback);
  }

  /**
   * Get current connection status.
   */
  getStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private setStatus(status: ConnectionStatus): void {
    if (this.connectionStatus === status) return;
    this.connectionStatus = status;
    this.statusCallbacks.forEach((cb) => cb(status));
  }

  private attemptReconnect(wasFastFailure: boolean): void {
    this.reconnectAttempts++;
    if (wasFastFailure) {
      this.fastFailureCount++;
    }

    if (this.reconnectAttempts > MAX_RETRIES) {
      const allFast = this.fastFailureCount >= this.reconnectAttempts;
      this.reconnectAttempts = 0;
      this.fastFailureCount = 0;
      this.setStatus(allFast ? 'auth-failure' : 'failed');
      return;
    }

    this.setStatus('reconnecting');

    const delay = Math.min(
      INITIAL_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1),
      MAX_DELAY_MS,
    );
    // Add jitter: ±25% of the delay
    const jitter = delay * 0.25 * (Math.random() * 2 - 1);
    const actualDelay = Math.max(0, delay + jitter);

    this.reconnectTimer = setTimeout(() => {
      // Read fresh token for reconnect attempt
      const token = localStorage.getItem('authToken') ?? undefined;
      this.connect(this.lastConnectUrl, token);
    }, actualDelay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// Export singleton instance
export const intelliHealthChatService = new IntelliHealthChatService();
